import { getOfferTypeClassificationType } from "./get-offer-type-classification";
import { getOfferPrice } from "./get-offer-price";
import type { OfferFormDataT } from "@/app/types/offers";

/**
 * Sort offers by classification (private/business), cheapest make, and price
 *
 * Sort order:
 * 1. Private offers (including mixed) come before business offers
 * 2. Within each group: sort by cheapest offer per make (make with cheapest offer comes first)
 * 3. Within same make: sort by price ascending
 * 4. Offers without price go to the end
 *
 * @param offers - Array of offers to sort
 * @returns Sorted array of offers
 *
 * @example
 * ```tsx
 * const sortedOffers = sortOffersByClassificationMakeAndPrice(offers);
 * ```
 */
export function sortOffersByClassificationMakeAndPrice(
  offers: OfferFormDataT[]
): OfferFormDataT[] {
  // Group offers by classification and make, and find cheapest price per make
  const makeCheapestPriceMap = new Map<string, number>();

  offers.forEach((offer) => {
    const makeId = offer.make?.id || "";
    if (!makeId) return;

    const price = getOfferPrice(offer);
    if (price === null) return;

    const currentCheapest = makeCheapestPriceMap.get(makeId);
    if (currentCheapest === undefined || price < currentCheapest) {
      makeCheapestPriceMap.set(makeId, price);
    }
  });

  // Helper to get cheapest price for a make (or Infinity if not found)
  const getCheapestPriceForMake = (offer: OfferFormDataT): number => {
    const makeId = offer.make?.id || "";
    return makeCheapestPriceMap.get(makeId) ?? Infinity;
  };

  return [...offers].sort((a, b) => {
    // Determine classification
    const classificationA = getOfferTypeClassificationType(a.offer_types);
    const classificationB = getOfferTypeClassificationType(b.offer_types);

    // Private comes before business
    const isPrivateA =
      classificationA === "private" || classificationA === "mixed";
    const isPrivateB =
      classificationB === "private" || classificationB === "mixed";

    if (isPrivateA && !isPrivateB) {
      return -1; // A (private) comes before B (business)
    }
    if (!isPrivateA && isPrivateB) {
      return 1; // B (private) comes before A (business)
    }

    // Within same group (both private or both business), sort by cheapest make price
    const cheapestPriceA = getCheapestPriceForMake(a);
    const cheapestPriceB = getCheapestPriceForMake(b);

    if (cheapestPriceA !== cheapestPriceB) {
      return cheapestPriceA - cheapestPriceB;
    }

    // Same make (or both have Infinity): sort by make name as fallback
    const makeNameA = a.make?.name || "";
    const makeNameB = b.make?.name || "";
    const makeComparison = makeNameA.localeCompare(makeNameB, "de");
    if (makeComparison !== 0) {
      return makeComparison;
    }

    // Same make: sort by price ascending
    const priceA = getOfferPrice(a);
    const priceB = getOfferPrice(b);

    // Offers without price go to the end
    if (priceA === null && priceB === null) return 0;
    if (priceA === null) return 1;
    if (priceB === null) return -1;

    return priceA - priceB;
  });
}
